import { useState, useEffect, useCallback, useRef } from 'react';
import { BuyingTemplate, useOffMarketForm } from './useOffMarketForm';
import { getCookie } from '../components/utils/utils';
import { localStore, sessionStore } from '../components/utils/safeStorage';

interface AddressComponents {
  houseNumber: string;
  street: string;
  city: string;
  region: string;
  country: string;
  countryCode?: string;
  postcode: string;
}

interface ExtendedFormData {
  address: string;
  location: { lat: number; lng: number } | null;
  buildingId: string | null;
  buildingGeometry: { type: string; coordinates: number[][][] } | null;
  buildingAddress: string;
  buildingIsSelected: boolean;
  selectedAddressComponents: AddressComponents | null;
  addressResults: any[];
  isDetailsExpanded: boolean;
}

interface UseLandingPageOffMarketFormProps {
  actionType: 'buy' | 'sell';
  isLoggedIn: boolean;
  isOpen: boolean;
}

export const useLandingPageOffMarketForm = ({
  actionType,
  isLoggedIn,
  isOpen
}: UseLandingPageOffMarketFormProps) => {
  // Use the base hook for common functionality
  const baseForm = useOffMarketForm({
    actionType,
    isLoggedIn,
    isOpenModal: isOpen,
    isLandingPage: true
  });

  // Additional state for landing page specific fields
  const [extendedData, setExtendedData] = useState<ExtendedFormData>({
    address: '',
    location: null,
    buildingId: null,
    buildingGeometry: null,
    buildingAddress: '',
    buildingIsSelected: false,
    selectedAddressComponents: null,
    addressResults: [],
    isDetailsExpanded: false
  });

  // Template state management - only for buying action
  const [buyingTemplate, setBuyingTemplate] = useState<BuyingTemplate | null>(null);
  const [hasTemplate, setHasTemplate] = useState<boolean>(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState<boolean>(false);
  const [isUsingTemplate, setIsUsingTemplate] = useState<boolean>(false);
  const [shouldSaveTemplate, setShouldSaveTemplate] = useState<boolean>(true);
  const [isTemplateAnimating, setIsTemplateAnimating] = useState<boolean>(false);

  // LocalStorage key for preserving extended form data
  const EXTENDED_FORM_DATA_STORAGE_KEY = `could_${actionType}_modal_extended_data`;
  console.log('Extended form storage key:', EXTENDED_FORM_DATA_STORAGE_KEY);

  // Save extended form data to localStorage
  const saveExtendedFormDataToLocalStorage = useCallback(() => {
    const dataToSave = {
      ...extendedData,
      timestamp: Date.now()
    };

    console.log('Saving extended form data with key:', EXTENDED_FORM_DATA_STORAGE_KEY, 'data:', dataToSave);
    localStore.setItemOrThrow(EXTENDED_FORM_DATA_STORAGE_KEY, JSON.stringify(dataToSave));
  }, [extendedData, EXTENDED_FORM_DATA_STORAGE_KEY]);

  // Template management functions
  const fetchBuyingTemplate = useCallback(async () => {
    if (!isLoggedIn || actionType !== 'buy') return;

    // Check session storage cache first
    const cacheKey = 'buying_template_cache';
    const cached = sessionStore.getJSON<{ timestamp: number; data: any }>(cacheKey);
    if (cached && typeof cached.timestamp === 'number' && cached.data) {
      const { timestamp, data } = cached;
      // Use cache if less than 10 minutes old
      if (Date.now() - timestamp < 10 * 60 * 1000) {
        console.log('Using cached buying template');
        setBuyingTemplate(data.template);
        setHasTemplate(data.hasTemplate);
        if (data.hasTemplate && data.template) {
          applyTemplateToForm(data.template);
        }
        return;
      }
    }

    setIsLoadingTemplate(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/buying-template`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBuyingTemplate(data.template);
        setHasTemplate(data.hasTemplate);

        // Cache the response
        sessionStore.setItem(cacheKey, JSON.stringify({
          timestamp: Date.now(),
          data
        }));

        if (data.hasTemplate && data.template) {
          applyTemplateToForm(data.template);
        }
      }
    } catch (error) {
      console.error('Error fetching buying template:', error);
    } finally {
      setIsLoadingTemplate(false);
    }
  }, [isLoggedIn, actionType]);

  const applyTemplateToForm = useCallback((template: BuyingTemplate) => {
    setIsUsingTemplate(true);
    setIsTemplateAnimating(true);

    // Apply template data to form with slight delay for animation
    setTimeout(() => {
      baseForm.setFormData(prev => ({
        ...prev,
        description: template.description || '',
        size: template.size || '',
        rooms: template.rooms || '',
        floor: template.floor || '',
        hasElevator: template.hasElevator || false,
        hasBalcony: template.hasBalcony || false,
        renovationStatus: template.renovationStatus || '',
      }));

      // End animation after fields are populated
      setTimeout(() => {
        setIsTemplateAnimating(false);
      }, 600);
    }, 100);

    console.log('Applied buying template to form');
  }, []);

  const saveBuyingTemplate = useCallback(async () => {
    if (!isLoggedIn || actionType !== 'buy') return false;

    try {
      const templateData = {
        description: baseForm.formData.description,
        size: baseForm.formData.size,
        rooms: baseForm.formData.rooms,
        floor: baseForm.formData.floor,
        hasElevator: baseForm.formData.hasElevator,
        hasBalcony: baseForm.formData.hasBalcony,
        renovationStatus: baseForm.formData.renovationStatus,
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/buying-template`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
        body: JSON.stringify(templateData),
      });

      if (response.ok) {
        const data = await response.json();
        setBuyingTemplate(data.template);
        setHasTemplate(true);

        // Clear cache to force refresh on next load
        sessionStore.removeItem('buying_template_cache');

        console.log('Buying template saved successfully');
        return true;
      }
    } catch (error) {
      console.error('Error saving buying template:', error);
    }
    return false;
  }, [isLoggedIn, actionType, baseForm.formData]);

  const deleteBuyingTemplate = useCallback(async () => {
    if (!isLoggedIn || actionType !== 'buy') return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/buying-template`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      });

      if (response.ok) {
        setBuyingTemplate(null);
        setHasTemplate(false);
        setIsUsingTemplate(false);
        // Ensure UI fields are cleared immediately
        baseForm.clearFormData();
        setShouldSaveTemplate(false);

        // Clear cache
        sessionStore.removeItem('buying_template_cache');

        console.log('Buying template deleted successfully');
        return true;
      }
    } catch (error) {
      console.error('Error deleting buying template:', error);
    }
    return false;
  }, [isLoggedIn, actionType, baseForm.clearFormData]);

  // Fetch template when modal opens for buy action
  useEffect(() => {
    if (isOpen && actionType === 'buy' && isLoggedIn && !restorationAttemptedRef.current) {
      fetchBuyingTemplate();
    }
  }, [isOpen, actionType, isLoggedIn, fetchBuyingTemplate]);

  // Restore extended form data from localStorage
  const restoreExtendedFormDataFromLocalStorage = useCallback((): boolean => {
    console.log('Attempting to restore extended form data with key:', EXTENDED_FORM_DATA_STORAGE_KEY);
    const savedDataStr = localStore.getItem(EXTENDED_FORM_DATA_STORAGE_KEY);
    if (!savedDataStr) {
      console.log('No saved extended form data found');
      return false;
    }

    try {
      const savedData = JSON.parse(savedDataStr);
      console.log('Found saved extended data:', savedData);

      // Check if data is less than 30 minutes old
      const thirtyMinutes = 30 * 60 * 1000;
      if (Date.now() - savedData.timestamp > thirtyMinutes) {
        console.log('Saved extended data is too old, discarding');
        localStore.removeItem(EXTENDED_FORM_DATA_STORAGE_KEY);
        return false;
      }

      // Restore extended data
      const restoredData = {
        address: savedData.address || '',
        location: savedData.location || null,
        buildingId: savedData.buildingId || null,
        buildingGeometry: savedData.buildingGeometry || null,
        buildingAddress: savedData.buildingAddress || '',
        buildingIsSelected: savedData.buildingIsSelected || false,
        selectedAddressComponents: savedData.selectedAddressComponents || null,
        addressResults: savedData.addressResults || [],
        isDetailsExpanded: savedData.isDetailsExpanded || false
      };

      console.log('Restoring extended data:', restoredData);
      setExtendedData(restoredData);

      // Clear the saved data after restoring
      localStore.removeItem(EXTENDED_FORM_DATA_STORAGE_KEY);

      return true;
    } catch (error) {
      console.error('Error restoring extended form data:', error);
      localStore.removeItem(EXTENDED_FORM_DATA_STORAGE_KEY);
      return false;
    }
  }, [EXTENDED_FORM_DATA_STORAGE_KEY]);

  // Override the save function to include extended data
  const saveAllFormData = useCallback(() => {
    console.log('Saving all form data (base + extended)');
    baseForm.saveFormDataToLocalStorage();
    saveExtendedFormDataToLocalStorage();
  }, [baseForm, saveExtendedFormDataToLocalStorage]);

  // Override the restore function to include extended data
  const restoreAllFormData = useCallback((): boolean => {
    console.log('Restoring all form data (base + extended)');
    const baseRestored = baseForm.restoreFormDataFromLocalStorage();
    const extendedRestored = restoreExtendedFormDataFromLocalStorage();
    return baseRestored || extendedRestored;
  }, [baseForm, restoreExtendedFormDataFromLocalStorage]);

  // Clear all form data
  const clearAllFormData = useCallback(() => {
    baseForm.clearFormData();
    setExtendedData({
      address: '',
      location: null,
      buildingId: null,
      buildingGeometry: null,
      buildingAddress: '',
      buildingIsSelected: false,
      selectedAddressComponents: null,
      addressResults: [],
      isDetailsExpanded: false
    });
    localStore.removeItem(EXTENDED_FORM_DATA_STORAGE_KEY);
  }, [baseForm, EXTENDED_FORM_DATA_STORAGE_KEY]);

  // Override handleBuyCredits to save extended data as well
  const handleBuyCredits = useCallback(async () => {
    // Save both base and extended form data before redirecting
    console.log('handleBuyCredits override: saving all form data before Stripe redirect');
    saveAllFormData();

    // Call the base handleBuyCredits which will handle the Stripe redirect
    return await baseForm.handleBuyCredits();
  }, [saveAllFormData, baseForm]);

  // Use ref to track if restoration has been attempted for this modal open session
  const restorationAttemptedRef = useRef(false);

  // Reset the restoration flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      restorationAttemptedRef.current = false;
    }
  }, [isOpen]);

  // Check for payment returns and restore all form data (SELL flow only)
  useEffect(() => {
    console.log('useLandingPageOffMarketForm useEffect triggered - isOpen:', isOpen, 'actionType:', actionType, 'restorationAttempted:', restorationAttemptedRef.current);
    if (isOpen && actionType === 'sell' && !restorationAttemptedRef.current) {
      const params = new URLSearchParams(window.location.search);
      const paymentStatus = params.get('payment');
      console.log('Landing page - Payment status:', paymentStatus);

      // Mark that we've attempted restoration for this session
      restorationAttemptedRef.current = true;

      if (paymentStatus === 'success' || paymentStatus === 'cancelled') {
        console.log('Payment return detected:', paymentStatus);
        const dataRestored = restoreAllFormData();
        if (dataRestored) {
          console.log(`All form data restored after ${paymentStatus} payment`);
        } else {
          console.log('Failed to restore form data after payment');
        }

        if (paymentStatus === 'success' && isLoggedIn) {
          baseForm.fetchEnhancementStatus();
        }

        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
      } else if (!paymentStatus) {
        // Try to restore data once on modal open
        const restored = restoreAllFormData();
        if (restored) {
          console.log('Data restored on modal open');
        }
      }
    }
  }, [isOpen, actionType, isLoggedIn, baseForm, restoreAllFormData]); // Include all dependencies

  return {
    ...baseForm,
    extendedData,
    setExtendedData,
    saveAllFormData,
    restoreAllFormData,
    clearAllFormData,
    handleBuyCredits, // Override with our version that saves extended data,
    // Template-related
    buyingTemplate,
    hasTemplate,
    isLoadingTemplate,
    isUsingTemplate,
    shouldSaveTemplate,
    setShouldSaveTemplate,
    isTemplateAnimating,
    fetchBuyingTemplate,
    saveBuyingTemplate,
    deleteBuyingTemplate
  };
};
